import { defineStore } from 'pinia';
import { notify } from 'notiwind';

export const useCardsStore = defineStore('cards', {
    state: () => ({
        activeCard: {
            id: null
        },
        errors: [],
        loading: false,
        isSyncing: false,
        lastSyncedAt: null,
        lastUpdatedAt: null,
        syncError: null,
        syncStatus: null,
        pollInterval: null,
        rewardsccLookupLoading: false,
        rewardsccLookupError: null,
    }),

    actions: {
        async updateCard(cardId, data) {
            try {
                const response = await ApiService.apiCall(`/admin/cards/${cardId}`, 'put', data)
                // check response code
                if (response?.status !== 200 && response?.data?.success !== true) {
                    notify({
                        group: "error",
                        title: "Error",
                        text: response.response?.data?.message
                            || response.response?.message
                            || response.data?.message
                            || response.message
                            || "Failed to update card"
                    })
                    return
                }
                notify({
                    group: "success",
                    title: "Success",
                    text: "Card updated successfully"
                })
                return response.data
            } catch (error) {
                notify({
                    group: "error",
                    title: "Error",
                    text: error.response?.data?.message || "Failed to update card"
                })
                throw error
            }
        },

        async createCard(data) {
            try {
                const response = await ApiService.apiCall('/admin/cards', 'post', data)
                if (response?.status !== 201 && response?.status !== 200) {
                    notify({
                        group: "error",
                        title: "Error",
                        text: response.response?.data?.message
                            || response.data?.message
                            || "Failed to create card"
                    })
                    return null
                }
                notify({
                    group: "success",
                    title: "Success",
                    text: "Card created successfully"
                })
                return response.data
            } catch (error) {
                throw error
            }
        },

        async lookupRewardsccCard(cardKey) {
            this.rewardsccLookupLoading = true
            this.rewardsccLookupError = null
            try {
                const response = await ApiService.apiCall('/admin/cards/rewardscc-lookup', 'post', { card_key: cardKey })
                if (response?.data?.success) {
                    return response.data.data
                }
                this.rewardsccLookupError = response?.data?.message || 'Card not found'
                return null
            } catch (error) {
                this.rewardsccLookupError = error.response?.data?.message || 'Failed to lookup card'
                notify({
                    group: "error",
                    title: "Error",
                    text: this.rewardsccLookupError
                })
                return null
            } finally {
                this.rewardsccLookupLoading = false
            }
        },

        async getCard(cardId) {
            try {
                const response = await ApiService.apiCall(`/cards/${cardId}`, 'get');
                const card = response.data?.data ?? response.data;
                this.setActiveCard(card);
                return card;
            } catch (error) {
                notify({
                    group: "error",
                    title: "Error",
                    text: error.response?.data?.message || "Failed to fetch card"
                });
                throw error;
            }
        },

        setActiveCard(card) {
            if (!card) {
                this.activeCard = { id: null };
                return;
            }
            this.activeCard = { ...card };
            this.setSyncDates(
                card.rewardscc_data?.last_imported_at,
                card.rewardscc_data?.updated_at
            );
        },

        clearErrors() {
            this.errors = [];
        },

        setError(err) {
            this.errors = Array.isArray(err) ? err : [err];
        },

        setFormError(key, message) {
            this.errors[key] = message;
        },

        // Sync-related actions
        async syncCardDetails(cardId) {
            this.isSyncing = true;
            this.syncError = null;
            this.syncStatus = 'syncing';
            this.clearErrors();

            try {
                const response = await ApiService.apiCall(`/admin/cards/${cardId}/sync-details`, 'post');

                if (response.data.success) {
                    notify({
                        group: 'success',
                        title: 'Success',
                        text: 'Card sync started successfully'
                    });
                    this.syncStatus = 'success';
                    this.startPolling();
                    return response.data;
                } else {
                    throw new Error(response.data.message || 'Failed to start card sync job');
                }
            } catch (error) {
                this.syncError = error.response?.data?.message || error.message;
                this.syncStatus = 'error';
                this.isSyncing = false;

                notify({
                    group: 'error',
                    title: 'Error',
                    text: this.syncError
                });

                throw error;
            }
        },

        async checkSyncStatus() {
            if (!this.activeCard.id) return;

            try {
                const response = await ApiService.apiCall(`/admin/cards/${this.activeCard.id}`, 'get');
                const card = response.data;

                if (card.rewardscc_data.last_imported_at !== this.lastSyncedAt ||
                    card.rewardscc_data.updated_at !== this.lastUpdatedAt) {

                    this.setActiveCard(card);

                    this.setSyncDates(
                        card.rewardscc_data.last_imported_at,
                        card.rewardscc_data.updated_at
                    );

                    notify({
                        group: 'success',
                        title: 'Success',
                        text: 'Card data updated successfully'
                    });
                    this.stopPolling();

                    return card;
                }

                // Si todavía está sincronizando pero no hay cambios aún
                if (this.isSyncing) {
                    return null;
                }

            } catch (error) {
                console.error('Error checking sync status:', error);
                this.syncStatus = 'error';
                this.stopPolling();

                notify({
                    group: 'error',
                    title: 'Error',
                    text: 'Error syncing card details'
                });
            }
        },
        addCardNameMatch(match) {
            this.activeCard.card_name_matches.push(match);
        },
        removeCardNameMatch(match) {
            this.activeCard.card_name_matches.splice(this.activeCard.card_name_matches.indexOf(match), 1);
        },
        startPolling() {
            this.stopPolling();
            this.pollInterval = setInterval(() => {
                this.isSyncing = true;
                this.checkSyncStatus();
            }, 5000); // Revisar cada 5 segundos
        },

        stopPolling() {
            if (this.pollInterval) {
                clearInterval(this.pollInterval);
                this.pollInterval = null;
            }
            this.isSyncing = false;
        },

        resetSyncStatus() {
            this.isSyncing = false;
            this.syncError = null;
            this.syncStatus = null;
            this.stopPolling();
        },

        setSyncDates(syncedAt, updatedAt) {
            this.lastSyncedAt = syncedAt;
            this.lastUpdatedAt = updatedAt;
        }
    },

    getters: {
        getSyncStatus: (state) => state.syncStatus,
        getSyncError: (state) => state.syncError,
        isCurrentlySyncing: (state) => state.isSyncing,
        getLastSyncedAt: (state) => state.lastSyncedAt,
        getLastUpdatedAt: (state) => state.lastUpdatedAt,
    }
});
